import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { myApplications } from '../../lib/jobsApi';
import type { SeekerApplication } from '../../lib/jobsApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import { candidateStageLabel } from '../../lib/candidateStageLabels';

/*
 * The CANDIDATE vocabulary, not the recruiter's. GET /api/applications reads
 * candidate_application_view, which collapses the stage in SQL: `shortlisted` arrives as
 * `in_review` and every rejection as `closed`, so this screen structurally cannot disclose that
 * someone was shortlisted or which stage they were cut from.
 *
 * The old map here was keyed on the RECRUITER tokens and read `app.status`, a key the server
 * stopped sending when applications.status became applications.stage. Every chip on this page
 * rendered blank: `STATUS_LABEL[undefined] || undefined`.
 */
const ApplicationStageChip: React.FC<{ stage: string; jobClosed: boolean }> = ({ stage, jobClosed }) => {
  const label = candidateStageLabel(stage);
  // `interviewing` and `hired` are the two the candidate should feel good about. There is no
  // saffron case for a shortlist here, and that is the point — they are not told about it.
  const isSaffron = stage === 'interviewing' || stage === 'hired';

  return (
    <div className="flex items-center gap-2">
      <span
        className="chip"
        style={
          isSaffron
            ? undefined
            : {
                backgroundColor: 'var(--color-hairline)',
                color: 'var(--color-ink-secondary)',
              }
        }
      >
        <span
          aria-hidden="true"
          className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{
            backgroundColor: isSaffron ? 'var(--color-saffron-press)' : 'var(--color-ink-tertiary)',
          }}
        />
        {label}
      </span>
      {jobClosed && <span className="text-xs text-ink-tertiary">Job closed</span>}
    </div>
  );
};

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">
      Couldn&apos;t load your applications.
    </h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const Applications: React.FC = () => {
  const [applications, setApplications] = useState<SeekerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — same pattern as MyJobs.tsx. A failed load must NOT
  // fall through to the empty state — "You haven't applied yet" would be a
  // trust-breaking lie when the real reason is a load failure.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const loadApplications = async () => {
      try {
        const res = await myApplications();
        if (!cancelled) {
          setApplications(res.applications);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Check your connection and try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadApplications();

    return () => {
      cancelled = true;
    };
  }, [loadNonce]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto" role="status">
        <span className="label-editorial block mb-3">Applications</span>
        <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">
          Loading…
        </h1>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <span className="label-editorial block mb-3">Applications</span>
        <ErrorBlock message={error} onRetry={() => setLoadNonce((n) => n + 1)} />
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="max-w-2xl mx-auto">
        <span className="label-editorial block mb-3">Applications</span>
        <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-12">
          Your applications
        </h1>

        <div className="bg-paper border border-hairline rounded-md p-8 text-center">
          <p className="text-ink mb-6">You haven't applied yet</p>
          <Link to="/jobs" className="btn-primary">
            Browse jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <span className="label-editorial block mb-3">Applications</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">
        Your applications
      </h1>

      <div className="space-y-4">
        {applications.map((app) => (
          <div key={app.id} className="bg-paper border border-hairline rounded-md p-6">
            {/* Header: Job title + company */}
            <div className="mb-4">
              <Link
                to={`/jobs/${app.jobId}`}
                className="inline-block hover:text-saffron-press transition-colors"
              >
                <h2 className="font-display text-lg font-medium text-ink tracking-tight">
                  {app.jobTitle}
                </h2>
              </Link>
              <p className="text-sm text-ink-secondary mt-1">{app.companyName}</p>
            </div>

            {/* Status and date row */}
            <div className="flex items-center justify-between mb-4">
              <ApplicationStageChip stage={app.stage} jobClosed={app.jobClosed} />
              <span className="text-xs text-ink-tertiary">{formatRelativeDate(app.createdAt)}</span>
            </div>

            {/* Submissions list */}
            {app.submissions && app.submissions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-hairline">
                <p className="text-xs font-medium text-ink-tertiary uppercase tracking-widest mb-3">
                  Submitted tasks
                </p>
                <ul className="space-y-2">
                  {app.submissions.map((submission) => (
                    <li key={submission.taskId} className="text-sm text-ink">
                      <span className="font-medium">{submission.taskTitle}</span>
                      <span className="text-ink-tertiary ml-2">({submission.type})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Applications;
